/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup from '@atlaskit/popup';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { Card as SmartCard } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type Sources } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { extractProductNameFromARI } from '../../../utils/ari';
import { transformIfConfluenceLink } from '../../../utils/url-transformer';

import messages from './messages';
import { SourceIcons } from './SourceButtonIcons';
import { SourcesPopupContent } from './SourcesPopupContent';

const styles = cssMap({
	box: {
		borderRadius: token('radius.small'),
		marginInline: token('space.0'),
		wordBreak: 'break-word',
	},

	boxPadding: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.0'),
	},

	boxMarginBlock: {
		marginBlock: token('space.200'),
	},

	sourcesButton: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		fontWeight: token('font.weight.medium'),
		fontFamily: token('font.family.body'),
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},

	sourcesButtonSelected: {
		borderColor: token('color.border.selected'),
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},

	sourcesButtonRadiusMedium: {
		borderRadius: token('radius.medium'),
	},
});

export type OnClickSource = (
	type: string,
	citedProduct: string | null,
	product: string | null,
	index?: number,
) => void;

export type SourceLinksProps = {
	sources: Sources;
	onClickSource?: OnClickSource;
	onSourcesButtonClicked?: () => void;
	conversationId?: string;
	showMarginBlock?: boolean;
	/**
	 * Whether to apply the default inner padding (8px top/right/bottom, 0 left)
	 * around the sources trigger. Defaults to `true` to preserve existing
	 * behaviour. Set to `false` when embedding the trigger in a host layout
	 * that controls its own spacing (e.g. an inline footer row).
	 */
	showPadding?: boolean;
	siteId?: string;
	productKey?: string;
	// Added for Resolution Panel in JSM
	shouldRenderPopupToParent?: boolean;
	/** Optional function to strip datasource params and/or apply XPC URL wrapping. */
	xpcWrapUrl?: (url: string, stripOnly: boolean) => string;
};

export const createFallbackComponent =
	(
		url: string,
		type: string,
		title?: string,
		ari?: string,
		product?: string,
		onClickSource?: OnClickSource,
		shouldAddConfluenceAttribution?: boolean,
		index?: number,
	) =>
	(): JSX.Element => {
		const transformedUrl = transformIfConfluenceLink(url, shouldAddConfluenceAttribution);
		return (
			<Link
				href={transformedUrl}
				target="_blank"
				rel="noopener noreferrer"
				onClick={() => {
					if (onClickSource) {
						onClickSource(type, extractProductNameFromARI(ari), product ?? null, index);
					}
				}}
			>
				{title || url}
			</Link>
		);
	};

export const SourcesView = function AnswerInfo({
	sources,
	onClickSource,
	onSourcesButtonClicked,
	conversationId,
	showMarginBlock = true,
	showPadding = true,
	siteId,
	productKey,
	shouldRenderPopupToParent = true,
	xpcWrapUrl,
}: SourceLinksProps): JSX.Element | null {
	di(SmartCard);
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const [isPopupOpen, setIsPopupOpen] = React.useState(false);
	const hasTrackedViewed = React.useRef(false);

	const assistanceServiceParams = React.useMemo<
		AssistanceServiceInputParam['assistanceServiceParams'] | undefined
	>(() => {
		if (!siteId || !productKey) {
			return undefined;
		}
		return {
			siteId,
			product: productKey as AssistanceServiceInputParam['assistanceServiceParams']['product'],
			touchpointSource: 'message-renderer-sources',
			experienceId: 'ai-mate',
		};
	}, [siteId, productKey]);

	const onClickSourceHandler = React.useCallback(
		(type: string, ari?: string, product?: string, index?: number) => {
			const citedProduct = extractProductNameFromARI(ari);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'sourceLink',
				attributes: {
					sourceType: type,
					conversationId,
					citedProduct,
					sourceProduct: product,
				},
			});

			if (onClickSource) {
				onClickSource(type, citedProduct, product ?? null, index);
			}
		},
		[sendAnalyticsEvent, onClickSource, conversationId],
	);

	const handlePopupOpen = React.useCallback(() => {
		if (!isPopupOpen) {
			onSourcesButtonClicked?.();
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'openSourcesPopup',
			});
			if (!hasTrackedViewed.current) {
				hasTrackedViewed.current = true;
				sendAnalyticsEvent({
					action: 'viewed',
					actionSubject: 'sources',
					actionSubjectId: 'sourcesViewed',
					attributes: {
						newSourcesUIEnabled: true,
					},
				});
			}
		}
		setIsPopupOpen(!isPopupOpen);
	}, [isPopupOpen, onSourcesButtonClicked, sendAnalyticsEvent]);

	const validSources = React.useMemo(
		() =>
			(sources ?? []).filter((s) => {
				if (!s?.url) return false;
				try {
					new URL(s.url);
					return true;
				} catch {
					return false;
				}
			}),
		[sources],
	);

	if (validSources.length === 0) {
		return null;
	}

	return (
		<Box
			xcss={cx(
				styles.box,
				showPadding && styles.boxPadding,
				showMarginBlock && styles.boxMarginBlock,
			)}
		>
			<Popup
				placement="top-start"
				isOpen={isPopupOpen}
				onClose={() => setIsPopupOpen(false)}
				content={() => (
					<SourcesPopupContent
						sources={validSources}
						onClickSourceHandler={onClickSourceHandler}
						onClickSource={onClickSource}
						onClose={() => setIsPopupOpen(false)}
						xpcWrapUrl={xpcWrapUrl}
					/>
				)}
				shouldRenderToParent={shouldRenderPopupToParent}
				trigger={(triggerProps) => (
					<Pressable
						{...triggerProps}
						xcss={cx(
							styles.sourcesButton,
							isPopupOpen && styles.sourcesButtonSelected,
							fg('rovo_chat_enable_twg_trace_team26') && styles.sourcesButtonRadiusMedium,
						)}
						onClick={handlePopupOpen}
						aria-label={formatMessage(messages.sourcesTitle, { sourceCount: validSources.length })}
						aria-expanded={isPopupOpen}
						aria-haspopup="dialog"
					>
						<Inline space="space.075" alignBlock="center">
							{fg('rovo_chat_enable_twg_trace_team26') ? (
								<>
									<Box>
										{formatMessage(messages.sourcesTitle, { sourceCount: validSources.length })}
									</Box>
									{assistanceServiceParams && (
										<SourceIcons
											sources={validSources}
											assistanceServiceParams={assistanceServiceParams}
										/>
									)}
								</>
							) : (
								<>
									{assistanceServiceParams && (
										<SourceIcons
											sources={validSources}
											assistanceServiceParams={assistanceServiceParams}
										/>
									)}
									<Box>
										{formatMessage(messages.sourcesTitle, { sourceCount: validSources.length })}
									</Box>
								</>
							)}
						</Inline>
					</Pressable>
				)}
			/>
		</Box>
	);
};
